import type { JSX } from "react";
import { useStatsFetch } from "../../api/hooks/useStatsFetch";
import type { SubscriptionStatsResponse } from "../../api/types";

/**
 * universe-subscription (Phase ε 第 4 段) Task 9.1:
 * Universe 別 subscription 統計の minimal 表示 section。 reader follow + 週次 digest 機構
 * (= 機構 M9 後続) の creator 観測 UI、 「フォロワー数 + 直近 7 日 follow 増減」 の 3 数値
 * を表示する。
 *
 * - props: `{ universeId }`
 * - useStatsFetch<SubscriptionStatsResponse> 経由で `GET /universes/:universeId/subscription-stats`
 * - data-testid="universe-subscription-stats-section"
 * - recentUnsubscribeCount は base 段で 0 fallback (= hard delete 履歴 loss、 future spec で
 *   audit table 導入時に active 化)、 「集計準備中」 と読み取れる表記に揃える
 * - endpoint は anonymous (= sibling spawn-stats / moderation-stats parity)、 useStatsFetch を
 *   credentials なし fetch で素直に呼ぶ
 *
 * 設計対応:
 * - design.md §Components Implementation Notes (= UI minimal section)
 * - requirements.md Req 6.5
 */

function resolveBaseUrl(): string {
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1"
  );
}

export interface SubscriptionStatsSectionProps {
  universeId: string;
}

export function SubscriptionStatsSection({
  universeId,
}: SubscriptionStatsSectionProps): JSX.Element {
  const state = useStatsFetch<SubscriptionStatsResponse>(
    `${resolveBaseUrl()}/universes/${encodeURIComponent(universeId)}/subscription-stats`,
    "subscription 統計",
  );

  return (
    <section
      data-testid="universe-subscription-stats-section"
      className="flex flex-col gap-1 px-2 py-2 bg-slate-900/40 rounded"
    >
      <h3 className="text-xs text-slate-300/80 font-medium">subscription 統計</h3>
      {state.isLoading ? (
        <p
          data-testid="universe-subscription-stats-loading"
          className="text-xs text-slate-200/60"
        >
          読み込み中…
        </p>
      ) : state.error !== null || state.data === null ? null : (
        <ul
          data-testid="universe-subscription-stats-list"
          className="text-xs flex flex-col gap-0.5 text-slate-100"
        >
          <li>
            <span className="text-slate-300/70">アクティブ subscriber: </span>
            {state.data.activeSubscriberCount} 人
          </li>
          <li>
            <span className="text-slate-300/70">直近 7 日 follow: </span>
            +{state.data.recentSubscriberCount}
          </li>
          <li>
            <span className="text-slate-300/70">直近 7 日 unfollow: </span>
            {/* MVP: recentUnsubscribeCount は audit 不在で 0 固定、 future spec で active 化 */}
            {state.data.recentUnsubscribeCount === 0
              ? "集計準備中"
              : `-${state.data.recentUnsubscribeCount}`}
          </li>
        </ul>
      )}
    </section>
  );
}
